import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import type { SignAndBroadcast } from "@levana/chain/signing"

import { notification } from "@future/notification"
import { AppError } from "@future/libs/error/AppError"
import { type Breadcrumb, Report } from "@future/libs/error/report"
import type { MarketConfig } from "@future/market/config/types"
import { EventKey } from "@perps/analytics/constants"
import type { ContextStoreProp } from "@future/context/store"

interface CallbackProps extends ContextStoreProp<"standard"> {
  walletAddress: string
  signAndBroadcast: SignAndBroadcast
  marketConfig: MarketConfig
}

export const useCrankCallback = () => {
  const { t } = useTranslation("common")

  return useCallback(
    async (props: CallbackProps) => {
      const { contextStore, walletAddress, signAndBroadcast, marketConfig } =
        props

      try {
        const response = await contextStore.getState().chain.client.execCrank({
          walletAddress,
          signAndBroadcast,
          marketConfig,
        })

        if (response) {
          const breadcrumb: Breadcrumb = {
            level: "info",
            type: "user",
            category: "crank",
            message: "crank completed",
            data: { hash: response.transactionHash },
          }

          notification.transactionSuccess({
            href: contextStore
              .getState()
              .chain.config.explorerHashUrl(response.transactionHash),
          })

          Report.addBreadcrumb(breadcrumb)
        }
      } catch (error: unknown) {
        const isOutOfGas = (() => {
          // This should _always_ be an AppError, but just in case, we'll defensively handle other kinds of errors too
          if (error instanceof AppError) {
            const context = error.presentationError.context
            if (context.messageVariant === "key") {
              switch (context.message) {
                case "chain.insufficientGas":
                case "chain.incorrectGasCalculated":
                case "chain.incorrectGasQuery":
                case "chain.incorrectGasSet":
                  return true
              }
            }
          }

          return false
        })()

        if (isOutOfGas) {
          notification.warning(t("crank.outOfGas"))
        } else {
          const appError = AppError.fromError(error, {
            text: "Failed to crank",
            analytics: {
              [EventKey.MARKET]: marketConfig.id,
            },
          })

          if (error instanceof Error) {
            notification.error(appError)
          } else {
            Report.error(appError)
          }
        }
      }
    },
    [t],
  )
}
